import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Music, FileText } from 'lucide-react';

// Samba/Percussion rhythm patterns
const RHYTHM_PATTERNS = {
  surdo: {
    name: 'Surdo (Bass Drum)',
    pattern: '1 . . . 2 . . . | 1 . . . 2 . . .',
    description: 'The heartbeat of samba - plays on beats 1 and 2',
    notation: `
    ┌─────────────────────────────────────┐
    │  ●       ●       ●       ●         │
    │  1   2   3   4   1   2   3   4     │
    │  B       B       B       B         │
    └─────────────────────────────────────┘
    `
  },
  caixa: {
    name: 'Caixa (Snare)',
    pattern: 'x x x x x x x x | x x x x x x x x',
    description: 'Continuous 16th notes with accents',
    notation: `
    ┌─────────────────────────────────────┐
    │  x x x x x x x x x x x x x x x x   │
    │  > . . > . . > . . > . . > . . >   │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    └─────────────────────────────────────┘
    `
  },
  tamborim: {
    name: 'Tamborim',
    pattern: '. x . x x . x . | . x . x x . x .',
    description: 'Classic telecoteco pattern',
    notation: `
    ┌─────────────────────────────────────┐
    │  . ● . ● ● . ● . . ● . ● ● . ● .   │
    │    te  le co te co   te le co te   │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    └─────────────────────────────────────┘
    `
  },
  agogo: {
    name: 'Agogô',
    pattern: 'H . L . H L . H | L . H . L H . L',
    description: 'Alternating high (H) and low (L) bells',
    notation: `
    ┌─────────────────────────────────────┐
    │  H   L   H L   H   L   H   L H   L │
    │  ●   ○   ● ○   ●   ○   ●   ○ ●   ○ │
    │  1   2   3 +   4   1   2   3 +   4 │
    └─────────────────────────────────────┘
    `
  },
  ganza: {
    name: 'Ganzá (Shaker)',
    pattern: 'x x x x x x x x | x x x x x x x x',
    description: 'Steady 16th note shaker pattern',
    notation: `
    ┌─────────────────────────────────────┐
    │  ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░ │
    │  Continuous shake - 16th notes      │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    └─────────────────────────────────────┘
    `
  },
  repinique: {
    name: 'Repinique (Lead)',
    pattern: '. . R . L R . . | R . L R . . R .',
    description: 'Call and response leader drum',
    notation: `
    ┌─────────────────────────────────────┐
    │  .   R   L R .   R   L R .   R     │
    │      ●   ○ ● .   ●   ○ ● .   ●     │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    │  R=rim, L=center, .=rest           │
    └─────────────────────────────────────┘
    `
  },
  pandeiro: {
    name: 'Pandeiro',
    pattern: 'T . t P t . P . | T . t P t . P .',
    description: 'Brasilian tambourine with thumb and finger strokes',
    notation: `
    ┌─────────────────────────────────────┐
    │  T   t P t   P   T   t P t   P     │
    │  ●   ○ ● ○   ●   ●   ○ ● ○   ●     │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    │  T=thumb, t=tip, P=palm            │
    └─────────────────────────────────────┘
    `
  },
  cuica: {
    name: 'Cuíca',
    pattern: '↑ . ↓ . ↑ ↓ . . | ↑ . ↓ . ↑ ↓ . ↑',
    description: 'Friction drum - up and down strokes',
    notation: `
    ┌─────────────────────────────────────┐
    │  ↑   ↓   ↑ ↓     ↑   ↓   ↑ ↓   ↑   │
    │  High/Low pitch variations          │
    │  1 e + a 2 e + a 3 e + a 4 e + a   │
    └─────────────────────────────────────┘
    `
  }
};

// Guitar chord shapes for common samba chords
const GUITAR_CHORDS = {
  'Am': { frets: 'x02210', name: 'A minor' },
  'Dm': { frets: 'xx0231', name: 'D minor' },
  'E7': { frets: '020100', name: 'E7' },
  'G': { frets: '320003', name: 'G major' },
  'C': { frets: 'x32010', name: 'C major' },
  'F': { frets: '133211', name: 'F major' },
  'Bm7b5': { frets: 'x2323x', name: 'Bm7♭5' },
  'A7': { frets: 'x02020', name: 'A7' },
};

export default function ScoreDisplay({ isOpen, onClose, currentBpm = 120 }) {
  const [activeTab, setActiveTab] = useState('percussion'); // percussion, chords, custom
  const [selectedPattern, setSelectedPattern] = useState('surdo');
  const [customScoreUrl, setCustomScoreUrl] = useState(null);
  const [customScoreName, setCustomScoreName] = useState('');
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const fileInputRef = useRef(null);
  const [beatHighlight, setBeatHighlight] = useState(0);
  const beatIntervalRef = useRef(null);

  // Animate beat highlight based on BPM
  useEffect(() => {
    if (isOpen && activeTab === 'percussion') {
      const interval = (60 / currentBpm) * 1000 / 4; // 16th note interval
      beatIntervalRef.current = setInterval(() => {
        setBeatHighlight(prev => (prev + 1) % 16);
      }, interval);
    }
    return () => {
      if (beatIntervalRef.current) clearInterval(beatIntervalRef.current);
    };
  }, [isOpen, currentBpm, activeTab]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setCustomScoreUrl(url);
      setCustomScoreName(file.name);
      setActiveTab('custom');
    }
  };

  const renderChordDiagram = (chord) => {
    const chordData = GUITAR_CHORDS[chord];
    if (!chordData) return null;

    return (
      <div className="bg-stone-800 rounded-lg p-4 text-center">
        <h4 className="font-bold mb-2">{chord}</h4>
        <div className="font-mono text-xs bg-stone-900 p-3 rounded inline-block">
          <div className="text-stone-500 mb-1">E A D G B E</div>
          <div className="text-lg tracking-widest">{chordData.frets}</div>
        </div>
        <p className="text-xs text-stone-400 mt-2">{chordData.name}</p>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-700">
        <div className="flex items-center gap-4">
          <Music className="w-6 h-6 text-amber-500" />
          <h2 className="text-xl font-bold">Score Display</h2>
          <span className="text-sm text-stone-400">BPM: {currentBpm}</span>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-stone-700 rounded-lg">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-700">
        <button
          onClick={() => setActiveTab('percussion')}
          className={`flex-1 py-3 font-bold ${activeTab === 'percussion' ? 'bg-amber-600 text-black' : 'bg-stone-800'}`}
        >
          🥁 Percussion Patterns
        </button>
        <button
          onClick={() => setActiveTab('chords')}
          className={`flex-1 py-3 font-bold ${activeTab === 'chords' ? 'bg-amber-600 text-black' : 'bg-stone-800'}`}
        >
          🎸 Chord Charts
        </button>
        <button
          onClick={() => setActiveTab('custom')}
          className={`flex-1 py-3 font-bold ${activeTab === 'custom' ? 'bg-amber-600 text-black' : 'bg-stone-800'}`}
        >
          📄 Custom Sheet
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Percussion Patterns */}
        {activeTab === 'percussion' && (
          <div className="space-y-4">
            {/* Pattern selector */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(RHYTHM_PATTERNS).map(key => (
                <button
                  key={key}
                  onClick={() => setSelectedPattern(key)}
                  className={`px-4 py-2 rounded-lg font-bold text-sm ${
                    selectedPattern === key ? 'bg-amber-500 text-black' : 'bg-stone-700'
                  }`}
                >
                  {RHYTHM_PATTERNS[key].name.split(' ')[0]}
                </button>
              ))}
            </div>

            {/* Selected pattern display */}
            {selectedPattern && RHYTHM_PATTERNS[selectedPattern] && (
              <div className="bg-stone-800 rounded-xl p-6">
                <h3 className="text-2xl font-bold text-amber-400 mb-2">
                  {RHYTHM_PATTERNS[selectedPattern].name}
                </h3>
                <p className="text-stone-400 mb-4">
                  {RHYTHM_PATTERNS[selectedPattern].description}
                </p>
                
                {/* Visual notation */}
                <div className="bg-stone-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="font-mono text-sm text-green-400 whitespace-pre">
                    {RHYTHM_PATTERNS[selectedPattern].notation}
                  </pre>
                </div>

                {/* Beat indicator */}
                <div className="mt-4">
                  <p className="text-xs text-stone-500 mb-2">Beat position (synced to BPM):</p>
                  <div className="flex gap-1">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                          beatHighlight === i ? 'bg-amber-500 text-black' : 
                          i % 4 === 0 ? 'bg-stone-600' : 'bg-stone-700'
                        }`}
                      >
                        {i % 4 === 0 ? (i / 4) + 1 : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pattern text */}
                <div className="mt-4 p-3 bg-stone-700 rounded-lg">
                  <p className="text-xs text-stone-400">Pattern:</p>
                  <p className="font-mono text-lg">{RHYTHM_PATTERNS[selectedPattern].pattern}</p>
                </div>
              </div>
            )}

            {/* All patterns overview */}
            <div className="mt-6">
              <h4 className="text-lg font-bold mb-3">Full Bateria (All Instruments)</h4>
              <div className="bg-stone-800 rounded-lg p-4 overflow-x-auto">
                <div className="font-mono text-xs space-y-1 min-w-[400px]">
                  <div className="text-stone-500">Beat:     1 e + a 2 e + a 3 e + a 4 e + a</div>
                  <div className="text-cyan-400">   Surdo:    ●       ●       ●       ●      </div>
                  <div className="text-green-400">  Caixa:    x x x x x x x x x x x x x x x x</div>
                  <div className="text-yellow-400">Tamborim:  . ● . ● ● . ● . . ● . ● ● . ● .</div>
                  <div className="text-purple-400"> Agogô:    H   L   H L   H   L   H   L H  </div>
                  <div className="text-pink-400">   Ganzá:    ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chord Charts */}
        {activeTab === 'chords' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold">Common Samba Chord Progressions</h3>
            
            {/* Common progressions */}
            <div className="bg-stone-800 rounded-lg p-4">
              <h4 className="font-bold text-amber-400 mb-3">ii-V-I in A minor (Bossa Nova)</h4>
              <div className="flex gap-4 flex-wrap">
                {['Bm7b5', 'E7', 'Am'].map(chord => renderChordDiagram(chord))}
              </div>
            </div>

            <div className="bg-stone-800 rounded-lg p-4">
              <h4 className="font-bold text-amber-400 mb-3">Basic Samba Progression</h4>
              <div className="flex gap-4 flex-wrap">
                {['Am', 'Dm', 'E7', 'Am'].map((chord, i) => (
                  <div key={i}>{renderChordDiagram(chord)}</div>
                ))}
              </div>
            </div>

            <div className="bg-stone-800 rounded-lg p-4">
              <h4 className="font-bold text-amber-400 mb-3">Major Key Progression</h4>
              <div className="flex gap-4 flex-wrap">
                {['C', 'G', 'Am', 'F'].map((chord, i) => (
                  <div key={i}>{renderChordDiagram(chord)}</div>
                ))}
              </div>
            </div>

            {/* Strum patterns */}
            <div className="bg-stone-800 rounded-lg p-4">
              <h4 className="font-bold text-amber-400 mb-3">Bossa Nova Strum Pattern</h4>
              <div className="bg-stone-900 p-4 rounded font-mono">
                <p className="text-green-400">Bass: × . × . × . × .</p>
                <p className="text-cyan-400">Chord:. × . × . × . ×</p>
                <p className="text-stone-500 text-sm mt-2">1 + 2 + 3 + 4 +</p>
              </div>
            </div>
          </div>
        )}

        {/* Custom Sheet Music */}
        {activeTab === 'custom' && (
          <div className="space-y-4">
            {!customScoreUrl ? (
              <div className="text-center py-20">
                <FileText className="w-16 h-16 mx-auto text-stone-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Upload Sheet Music</h3>
                <p className="text-stone-400 mb-6">Support for PDF, PNG, JPG images</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 bg-amber-500 text-black rounded-lg font-bold flex items-center gap-2 mx-auto"
                >
                  <Upload className="w-5 h-5" />
                  Choose File
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Controls */}
                <div className="flex items-center justify-between bg-stone-800 rounded-lg p-3">
                  <span className="font-medium truncate flex-1">{customScoreName}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setZoom(z => Math.max(50, z - 25))}
                      className="p-2 bg-stone-700 rounded"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-sm w-12 text-center">{zoom}%</span>
                    <button
                      onClick={() => setZoom(z => Math.min(200, z + 25))}
                      className="p-2 bg-stone-700 rounded"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setCustomScoreUrl(null); setCustomScoreName(''); }}
                      className="p-2 bg-red-600 rounded ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Score display */}
                <div className="bg-white rounded-lg p-4 overflow-auto max-h-[60vh]">
                  {customScoreUrl.endsWith('.pdf') ? (
                    <iframe
                      src={customScoreUrl}
                      className="w-full h-[500px]"
                      title="Sheet Music"
                    />
                  ) : (
                    <img
                      src={customScoreUrl}
                      alt="Sheet Music"
                      style={{ width: `${zoom}%` }}
                      className="mx-auto"
                    />
                  )}
                </div>

                {/* Page navigation for multi-page */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 bg-stone-700 rounded disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 bg-stone-700 rounded disabled:opacity-50"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
